import { useEffect, useState } from "react";
import axios from "axios";
import { FadeUp, MaskedReveal, SectionLabel } from "@/components/Motion";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReadingRoom() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/reading-room`)
      .then((r) => {
        setArticles(r.data.articles);
        setCategories(["All", ...r.data.categories]);
      })
      .finally(() => setLoading(false));
  }, []);

  const visible = active === "All" ? articles : articles.filter((a) => a.category === active);
  const [featured, ...rest] = visible;

  return (
    <div className="pt-10 md:pt-16" data-testid="reading-room-page">
      <section className="editorial-container">
        <SectionLabel number="No. 002" label="The Reading Room" />
        <h1 className="mt-6 font-serif text-[13vw] sm:text-[9vw] md:text-[7vw] lg:text-[6rem] leading-[1] tracking-tight text-ink pb-4">
          <MaskedReveal text="A curated" as="span" className="block" delay={0.1} animate />
          <MaskedReveal text="library." as="span" className="block italic text-sage" delay={0.4} animate />
        </h1>
        <FadeUp delay={0.8}>
          <p className="mt-8 font-serif text-xl md:text-2xl leading-[1.4] text-ink/75 max-w-2xl">
            The Reading Room is a curated index of pregnancy and motherhood writing we return to —
            drawn from trusted publications and external sources. Every piece is attributed to its
            original publisher and linked out to the source.
          </p>
        </FadeUp>
        <FadeUp delay={1.0}>
          <div className="mt-10 border-t border-ink/15 pt-8 max-w-2xl">
            <div className="overline text-ink/50">A note</div>
            <p className="mt-4 font-serif italic text-xl md:text-2xl text-ink/80 leading-relaxed">
              The most tender writing — the anonymous letters from real mothers — lives only in the
              printed edition. We do not publish them online. To read them, you'll have to open an
              envelope.
            </p>
          </div>
        </FadeUp>
      </section>

      {/* Elegant category rule instead of chunky filter pills */}
      <section className="mt-16 md:mt-20 editorial-container">
        <div className="hairline pt-6" data-testid="category-filters">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`overline transition-colors ${
                  active === c ? "text-ink" : "text-ink/40 hover:text-ink/70"
                }`}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
                {active === c && <span className="ml-2 inline-block h-1 w-1 bg-terracotta rounded-full align-middle" />}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured piece — magazine-style opening spread */}
      {loading ? (
        <section className="mt-20 editorial-container">
          <p className="font-serif italic text-ink/60">Loading…</p>
        </section>
      ) : featured ? (
        <section className="mt-20 md:mt-28 editorial-container" data-testid="featured-article">
          <a
            href={featured.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
            data-testid={`article-${featured.id}`}
          >
            <div className="grid grid-cols-12 gap-x-6 items-end">
              <div className="col-span-12 md:col-span-8">
                <div className="overline text-ink/60">{featured.category} · Featured</div>
                <h2
                  className={`mt-6 font-serif italic text-5xl md:text-6xl lg:text-[5.5rem] leading-[1.02] tracking-tight text-ink transition-colors ${
                    featured.accent === "terracotta"
                      ? "group-hover:text-terracotta"
                      : featured.accent === "sage"
                        ? "group-hover:text-sage"
                        : "group-hover:text-ink"
                  }`}
                >
                  {featured.title}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-4 mt-8 md:mt-0">
                <p className="font-serif text-xl leading-[1.5] text-ink/75">{featured.blurb}</p>
                <div className="mt-8 flex items-center justify-between">
                  <div className="overline text-ink/50">Read at · {featured.source}</div>
                  <ArrowUpRight
                    size={22}
                    className="text-ink/60 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1"
                  />
                </div>
              </div>
            </div>
          </a>
        </section>
      ) : null}

      {/* The rest — quiet two-column editorial list */}
      <section className="mt-24 md:mt-32 editorial-container">
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-20">
            {rest.map((a, i) => (
              <FadeUp key={a.id} delay={(i % 2) * 0.06}>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                  data-testid={`article-${a.id}`}
                >
                  <div className="overline text-ink/50">{a.category}</div>
                  <h3
                    className={`mt-5 font-serif text-3xl md:text-4xl leading-[1.1] tracking-tight text-ink transition-colors ${
                      a.accent === "terracotta"
                        ? "group-hover:text-terracotta"
                        : a.accent === "sage"
                          ? "group-hover:text-sage"
                          : "group-hover:text-ink"
                    }`}
                  >
                    {a.title}
                  </h3>
                  <p className="mt-5 font-serif text-lg text-ink/70 leading-relaxed max-w-lg">{a.blurb}</p>
                  <div className="mt-8 flex items-center justify-between max-w-lg">
                    <div className="overline text-ink/45">— {a.source}</div>
                    <ArrowUpRight
                      size={18}
                      className="text-ink/60 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1"
                    />
                  </div>
                </a>
              </FadeUp>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
